import {
  Close,
  Delete,
  Save,
  Visibility,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import {
  LIST_SAVED_VIEW_SHARING_PRIVATE,
} from "../../../constants";
import { hostname, parseError } from "../../../utils";

export interface SavedView {
  id: string;
  name: string;
  filterModel: any;
  sortModel: any[];
  columnVisibilityModel: Record<string, boolean>;
  sharing: string;
  isOwner: boolean;
  created_date?: string;
}

interface SavedViewDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  getToken: () => Promise<string>;
  eventListener: (event: any) => void;
  currentFilterModel: any;
  currentSortModel: any[];
  currentColumnVisibilityModel: Record<string, boolean>;
  onApplyView: (view: SavedView) => void;
}

const SavedViewDialog: React.FC<SavedViewDialogProps> = ({
  open,
  onClose,
  companyId,
  getToken,
  eventListener,
  currentFilterModel,
  currentSortModel,
  currentColumnVisibilityModel,
  onApplyView,
}) => {
  const [savedViews, setSavedViews] = useState<SavedView[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newViewName, setNewViewName] = useState("");
  const [showSaveForm, setShowSaveForm] = useState(false);

  const fetchSavedViews = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/list-saved-views?companyId=${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSavedViews(response.data);
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchSavedViews();
      setShowSaveForm(false);
      setNewViewName("");
    }
  }, [open]);

  const handleSaveView = async () => {
    if (!newViewName.trim()) return;

    setSaving(true);
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/list-saved-views`,
        {
          companyId,
          name: newViewName.trim(),
          filterModel: currentFilterModel,
          sortModel: currentSortModel,
          columnVisibilityModel: currentColumnVisibilityModel,
          sharing: LIST_SAVED_VIEW_SHARING_PRIVATE,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewViewName("");
      setShowSaveForm(false);
      fetchSavedViews();
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteView = async (viewId: string) => {
    try {
      const token = await getToken();
      await axios.delete(
        `${hostname}/list-saved-views/${viewId}?companyId=${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchSavedViews();
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    }
  };

  const handleApplyView = (view: SavedView) => {
    onApplyView(view);
    onClose();
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Saved Views</Typography>
          <IconButton onClick={handleClose} size="small" disabled={saving}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Save and load list views with custom filters, sorting, and column
          visibility. Views can be applied to any list.
        </Typography>

        {/* Save current view section */}
        {!showSaveForm ? (
          <Button
            startIcon={<Save />}
            variant="outlined"
            onClick={() => setShowSaveForm(true)}
            sx={{ mb: 2 }}
            fullWidth
          >
            Save Current View
          </Button>
        ) : (
          <Box sx={{ mb: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Save Current View
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="View Name"
              value={newViewName}
              onChange={(e) => setNewViewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newViewName.trim()) {
                  handleSaveView();
                }
              }}
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                onClick={() => {
                  setShowSaveForm(false);
                  setNewViewName("");
                }}
              >
                Cancel
              </Button>
              <LoadingButton
                size="small"
                variant="contained"
                onClick={handleSaveView}
                loading={saving}
                disabled={!newViewName.trim()}
              >
                Save
              </LoadingButton>
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* List of saved views */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Your Saved Views
        </Typography>
        {loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        ) : savedViews.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No saved views yet. Save your current view to get started.
          </Typography>
        ) : (
          <List sx={{ p: 0 }}>
            {savedViews.map((view) => (
              <ListItem
                key={view.id}
                disablePadding
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <ListItemButton onClick={() => handleApplyView(view)}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {view.name}
                        {!view.isOwner && (
                          <Chip
                            label="Shared"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box
                        component="span"
                        sx={{ display: "flex", gap: 1, mt: 0.5 }}
                      >
                        {view.filterModel?.items?.length > 0 && (
                          <Chip
                            label={`${view.filterModel.items.length} filter${view.filterModel.items.length > 1 ? "s" : ""}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {view.sortModel?.length > 0 && (
                          <Chip
                            label="Sorted"
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {view.columnVisibilityModel &&
                          Object.values(view.columnVisibilityModel).some(
                            (v) => v === false
                          ) && (
                            <Chip
                              label="Hidden columns"
                              size="small"
                              variant="outlined"
                            />
                          )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApplyView(view);
                      }}
                      title="Apply view"
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                    {view.isOwner && (
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteView(view.id);
                        }}
                        title="Delete view"
                        sx={{ ml: 1 }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </ListItemSecondaryAction>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SavedViewDialog;
